import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import encapsulatedStyles from './styles/outline-container.encapsulated.css?inline';
import globalStyles from './styles/outline-container.global.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export const containerWidths = ['default', 'full-bleed', 'narrow'] as const;
export type ContainerWidth = (typeof containerWidths)[number];

export const containerTextThemes = ['dark', 'light'] as const;
export type ContainerTextTheme = (typeof containerWidths)[number];

export const containerLayouts = ['default', 'two-col'] as const;
export type ContainerLayout = (typeof containerLayouts)[number];

export const  backgroundColors = ['white', 'gray', 'blue', 'navy', 'gold', 'gradient-blue', 'gradient-gold', 'transparent'] as const;
export type ContainerBgColor = (typeof backgroundColors)[number];

const componentName='outline-container';

/**
 * The OutlineContainer component
 * @element outline-container
 *
 * @slot - default
 * @attr top-margin
 * @attr bottom-margin
 * @attr padding
 * @attr justify-end
 * @attr container-width
 * @attr background-color
 */
@customElement('outline-container')
export class OutlineContainer extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
    globalCSS: globalStyles,
  });

  @property({
    type: String,
    reflect: true,
    attribute: 'top-margin',
  })
  topMargin: string | undefined;

  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-margin',
  })
  bottomMargin: string | undefined;

  /**
   * Left/Right padding
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: string | undefined;

  /**
   * Top/Bottom padding
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'y-padding',
  })
  yPadding: string | undefined;

  /**
   * Sets component content to `align-items: center` if `content-constraint` not set.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'center-content',
  })
  centerContent:boolean;

  /**
   * If 'light' sets content text color to --outline-white, if 'dark' sets color to --outline-black
   */
  @property({
    type: String, 
    reflect: true, 
    attribute: 'text-theme'
  })
  textTheme: ContainerTextTheme

  /**
   * Default single column or 1/4 : 3/4 `two-col` layout above medium screen size.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'layout',
  })
  layout: ContainerLayout = 'default';

  /**
   * Sets container width to `default` or `full-bleed`
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'container-width',
  })
  containerWidth: ContainerWidth = 'default';

  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: ContainerBgColor = 'transparent';

  @property({
    type: String,
    reflect: true,
    attribute: 'background-img',
  })
  backgroundImage: string;

  /**
   * Sets horizontal gap between slotted elements.
   * Any valid css spacing/size value. EX: '1rem', '30px', 'var(--spacing-4)', etc
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap: string;

  /**
   * Sets vertical gap between slotted contents.
   * Any valid css spacing/size value. EX: '1rem', '30px', 'var(--spacing-4)', etc
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'column-gap',
  })
  colGap:string | undefined;

  /**
   * Sets a custom max-width of the slotted content.
   * Any valid css spacing/size value. EX: '1rem', '30px', 'var(--outline-container-article-contnet-max-width)', etc
   */
  @property({type: String, attribute: 'content-constraint'})
  contentConstraint:string | undefined;

  updated(){
    this.setStyleTag()
  }

  render(): TemplateResult {
    const containerWidth = this.containerWidth;
    const classes = {
      container: true,
      [containerWidth]: true,
      [this.backgroundColor]: this.backgroundColor,
      'gap-set': !this.contentConstraint && this.colGap || !this.contentConstraint && this.rowGap,
      'center-content': !this.contentConstraint && this.centerContent,
      'background-image': this.backgroundImage,
    };

    const containerClass = {
      'container-wrapper': true,
    };
    
    return html`
      ${this.setStyleTag()}
      <div class="${classMap(containerClass)}">
        <div class="${classMap(classes)}">
          ${this.contentTemplate()}
        </div>
      </div>`;
      }
  
  contentTemplate() {
    const constrainedClasses = {
      'constrained-content': true,
      'gap-set': this.colGap || this.rowGap,
      'center-content': !!this.contentConstraint && !!this.centerContent,
    }

    if(this.contentConstraint) {
      return html `
          <div class="${classMap(constrainedClasses)}">
            <slot></slot>
          </div>`  
      } 
      if(this.layout === 'two-col') {
        return html`
          <div class="two-col-container">
            <div class="col-left">
              <slot name='col-left'></slot>
            </div>
            <div class="col-right">
              <slot name='col-right'></slot>
            </div>
          </div>`
      }
      else {
      return html `<slot></slot>`
    }
  }

  setStyleTag() {
    const topMargin = this.topMargin ? this.topMargin : `var(--outline-container-mt-default)`;
    const bottomMargin = this.bottomMargin ? this.bottomMargin : `var(--outline-container-mb-default)`;
    const xPadding = this.xPadding ? this.xPadding : `var(--outline-container-x-padding-default)`;
    const yPadding = this.yPadding ? this.yPadding : `var(--outline-container-y-padding-default)`;
    const textTheme = this.textTheme ? this.textTheme : null;
    const bgImage = this.backgroundImage ? this.backgroundImage: null;
    const constraint = this.contentConstraint ? this.contentConstraint : '100%';
    return html`
      <style>
        :host {
          /* container variable values are set in outline.theme.css */
          --container-top-margin: ${topMargin};
          --container-bottom-margin: ${bottomMargin};
          --container-x-padding: ${xPadding};
          --container-y-padding: ${yPadding};
          --container-row-gap: ${this.rowGap};
          --container-col-gap: ${this.colGap};
          --container-background-img: url(${bgImage});
          --container-text-theme: ${textTheme};
          --container-interior-content-max-width: ${constraint};
        }
      </style>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
  [componentName]: OutlineContainer;
  }
}
